import { isColumnarSheet, type WorkbookDefinition } from '@erp/shared-types';
import type { SchemaGroupReading } from './workbookSchemaGroup';
import { sheetKind } from '@erp/workbooks';

/** Where a File or Source's rows come from, as the Data section describes it. */
export type WorkbookDataOrigin =
  | {
      kind: 'file';
      fileName: string;
      sheets: readonly string[];
      uploadedAt?: string;
    }
  | {
      kind: 'file-sheet';
      fileId: string;
      /** Undefined when the File was removed. */
      fileName?: string;
      sheetName: string;
      headerRow: number;
    }
  | { kind: 'pasted' }
  | { kind: 'mapping'; name: string }
  | { kind: 'stateful-table'; name: string }
  | { kind: 'data-source'; name: string }
  /** The Data Sources of a schema combined into one table, as the catalog names them. */
  | { kind: 'schema-group'; reading: SchemaGroupReading; labelColumn?: string }
  /** A Data Asset: its Latest version unless the Source pins one. */
  | { kind: 'data-asset'; name: string; pinned: boolean }
  | { kind: 'object'; object: string }
  /** An Entity, narrowed by `filterCount` filters. */
  | { kind: 'entity'; name: string; filterCount: number };

/** An Item that reads a File or Source: a Source reading a File, or a Worksheet. */
export interface WorkbookDataReader {
  id: string;
  name: string;
  /** How it reads: a file Source's sheet, a Worksheet's input, or a Lookup. */
  via: 'source' | 'input' | 'lookup';
}

/** One File or Source in the Process tab's Data section. */
export interface WorkbookDataItem {
  id: string;
  name: string;
  origin: WorkbookDataOrigin;
  /** Undefined until known: inline tables always, other Sources once a preview was loaded. */
  columns?: readonly string[];
  rowCount?: number;
  readBy: readonly WorkbookDataReader[];
}

/** Where the Workbook's rows go: a File Output and the Worksheets its tabs hold. */
export interface WorkbookFileSink {
  itemId: string;
  fileName: string;
  /** The tabs' Worksheet names, in tab order. */
  sheetNames: readonly string[];
}

/** What the Data section knows beyond the Definition, without fetching anything. */
export interface WorkbookDataContext {
  /** The name of a Data Source link, Data Mapping, Data Asset or Entity in the catalog. */
  catalogName(id: string): string | undefined;
  /** What a group reads: its schema's complete membership, not the catalog's capped view. */
  schemaGroupReading(
    source: Extract<Source, { kind: 'schema-group' }>,
  ): SchemaGroupReading;
  /** An already-loaded preview of a non-inline Source. */
  preview(
    sourceId: string,
  ): { columns: string[]; rowCount: number } | undefined;
}

type Source = WorkbookDefinition['sources'][number];

function sourceOrigin(
  definition: WorkbookDefinition,
  source: Source,
  context: WorkbookDataContext,
): WorkbookDataOrigin {
  switch (source.kind) {
    case 'file':
      return {
        kind: 'file-sheet',
        fileId: source.fileId,
        fileName: definition.files.find((file) => file.id === source.fileId)
          ?.fileName,
        sheetName: source.sheetName,
        headerRow: source.headerRow,
      };
    case 'input':
      return { kind: 'pasted' };
    case 'object':
      return { kind: 'object', object: source.query.base };
    case 'schema-group':
      return {
        kind: 'schema-group',
        reading: context.schemaGroupReading(source),
        labelColumn: source.labelColumn,
      };
    default:
      return catalogOrigin(source, context);
  }
}

/** A Source read from the catalog, named as the catalog names it. */
function catalogOrigin(
  source: Exclude<
    Source,
    { kind: 'file' | 'input' | 'object' | 'schema-group' }
  >,
  context: WorkbookDataContext,
): WorkbookDataOrigin {
  const name = (id: string) => context.catalogName(id) ?? source.name;
  switch (source.kind) {
    case 'mapping':
      return { kind: 'mapping', name: name(source.mappingId) };
    case 'stateful-table':
      return { kind: 'stateful-table', name: name(source.tableId) };
    case 'data-source':
      return { kind: 'data-source', name: name(source.linkId) };
    case 'data-asset':
      return {
        kind: 'data-asset',
        name: name(source.dataAssetId),
        pinned: source.versionId !== undefined,
      };
    case 'forecast-snapshots':
      return { kind: 'data-source', name: source.name };
    case 'entity':
      return {
        kind: 'entity',
        name: name(source.entity),
        filterCount: source.filters?.length ?? 0,
      };
  }
}

function worksheetReaders(
  definition: WorkbookDefinition,
  id: string,
): WorkbookDataReader[] {
  return definition.sheets.flatMap((sheet): WorkbookDataReader[] => {
    if (sheetKind(sheet).input() === id)
      return [{ id: sheet.id, name: sheet.name, via: 'input' }];
    return isColumnarSheet(sheet) &&
      sheet.lookups.some((lookup) => lookup.sourceId === id)
      ? [{ id: sheet.id, name: sheet.name, via: 'lookup' }]
      : [];
  });
}

/** Every File, then every Source, with its origin, known shape and readers. */
export function workbookDataItems(
  definition: WorkbookDefinition,
  context: WorkbookDataContext,
): WorkbookDataItem[] {
  const files = definition.files.map((file): WorkbookDataItem => ({
    id: file.id,
    name: file.name,
    origin: {
      kind: 'file',
      fileName: file.fileName,
      sheets: file.sheets,
      uploadedAt: file.uploadedAt,
    },
    readBy: definition.sources.flatMap((source): WorkbookDataReader[] =>
      source.kind === 'file' && source.fileId === file.id
        ? [{ id: source.id, name: source.name, via: 'source' }]
        : [],
    ),
  }));
  const sources = definition.sources.map((source): WorkbookDataItem => {
    const table =
      source.kind === 'input'
        ? { columns: source.table.columns, rowCount: source.table.rows.length }
        : context.preview(source.id);
    return {
      id: source.id,
      name: source.name,
      origin: sourceOrigin(definition, source, context),
      columns: table?.columns,
      rowCount: table?.rowCount,
      readBy: worksheetReaders(definition, source.id),
    };
  });
  return [...files, ...sources];
}

/** Each File Output with a tab, as a sink. Reports chart Results; they write no file. */
export function workbookFileSinks(
  definition: WorkbookDefinition,
): WorkbookFileSink[] {
  const names = new Map(
    definition.sheets.map((sheet) => [sheet.id, sheet.name]),
  );
  return definition.outputs.flatMap((output): WorkbookFileSink[] =>
    output.kind === 'file' && output.tabs.length
      ? [
          {
            itemId: output.id,
            fileName: output.fileName,
            sheetNames: output.tabs.map((tab) => names.get(tab.sheetId) ?? ''),
          },
        ]
      : [],
  );
}

/** The first `limit` names and how many more there are. */
export function truncatedNames(
  names: readonly string[],
  limit = 6,
): { shown: string; more: number } {
  return {
    shown: names.slice(0, limit).join(', '),
    more: Math.max(0, names.length - limit),
  };
}
